import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Swords,
  Plus,
  Trophy,
  Gamepad2,
  Users,
  MessageSquare,
} from 'lucide-react';
import { GAMES } from '@/lib/gameConfig';
import { GameIcon } from '@/components/ui/GameIcon';

interface SidebarProps {
  className?: string;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Challenges', href: '/wagers', icon: Swords },
  { name: 'New Challenge', href: '/wagers/create', icon: Plus },
  { name: 'Friends', href: '/friends', icon: Users },
  { name: 'Messages', href: '/messages', icon: MessageSquare },
];

// Deduplicate games by name (some have multiple codes like lol/league_of_legends)
const sidebarGames = GAMES.filter((g, i, arr) =>
  arr.findIndex((x) => x.name === g.name) === i
);

export function Sidebar({ className }: SidebarProps) {
  const location = useLocation();

  return (
    <aside
      className={cn(
        'fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-64 border-r bg-background/80 backdrop-blur-xl',
        className
      )}
    >
      <div className="flex h-full flex-col p-4">
        <nav className="flex flex-col gap-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="mt-4 flex min-h-0 flex-1 flex-col border-t pt-4">
          <div className="mb-2 flex items-center gap-2 px-3">
            <Gamepad2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Arenas
            </span>
          </div>
          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto pr-1">
            {sidebarGames.map((game) => {
              const isActive = location.pathname === `/lobbies/${game.code}`;
              return (
                <Link
                  key={game.code}
                  to={`/lobbies/${game.code}`}
                  className={cn(
                    'flex shrink-0 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <GameIcon gameCode={game.code} size="sm" />
                  {game.shortName}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="shrink-0 mt-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-400" />
            <span className="font-semibold">Leaderboard</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            See top players this week
          </p>
          <Link
            to="/leaderboard"
            className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
          >
            View Rankings →
          </Link>
        </div>
      </div>
    </aside>
  );
}
